import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Share } from "lucide-react";
import { toast } from "sonner";
import useInstallPrompt from "@/hooks/useInstallPrompt";

/**
 * Auto-appearing bottom banner that nudges installation.
 * - Android/Desktop Chrome: shows "Install" button as soon as beforeinstallprompt fires
 * - iOS Safari: shows "Tap Share → Add to Home Screen" after 3 seconds
 * - Dismissal is remembered for 14 days
 */
export default function InstallPrompt() {
  const { canInstall, isIOS, isStandalone, promptInstall } = useInstallPrompt();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone) return;

    const dismissedAt = Number(localStorage.getItem("hn_pwa_dismissed") || 0);
    if (dismissedAt && Date.now() - dismissedAt < 14 * 24 * 60 * 60 * 1000) return;

    if (canInstall) {
      setVisible(true);
      return;
    }
    if (isIOS) {
      const t = setTimeout(() => setVisible(true), 3000);
      return () => clearTimeout(t);
    }
  }, [canInstall, isIOS, isStandalone]);

  const install = async () => {
    const outcome = await promptInstall();
    if (outcome === "accepted") toast.success("HostelNow installed!");
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem("hn_pwa_dismissed", String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed left-3 right-3 bottom-24 md:bottom-6 md:left-auto md:right-6 md:max-w-sm z-[9998] rounded-2xl shadow-2xl border border-orange-200 bg-white overflow-hidden"
    >
      <div className="p-4 flex items-start gap-3">
        <img src="/icon-192.png" alt="HostelNow" className="w-12 h-12 rounded-xl flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-slate-900 text-base leading-tight">
            Install HostelNow
          </div>
          {isIOS ? (
            <div className="text-xs text-slate-600 mt-1 flex items-center gap-1 flex-wrap">
              Tap <Share size={12} className="inline text-sky-600" /> then
              <span className="font-semibold">"Add to Home Screen"</span>
            </div>
          ) : (
            <div className="text-xs text-slate-600 mt-1">
              Add to your home screen for one-tap booking & instant updates.
            </div>
          )}
          <div className="flex gap-2 mt-3">
            {canInstall && (
              <Button
                data-testid="pwa-install-btn"
                onClick={install}
                className="rounded-full h-8 px-4 bg-orange-600 hover:bg-orange-700 text-white text-xs gap-1"
              >
                <Download size={14} /> Install
              </Button>
            )}
            <button
              data-testid="pwa-dismiss-btn"
              onClick={dismiss}
              className="text-xs text-slate-500 hover:text-slate-800 h-8 px-3"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Close"
          className="text-slate-400 hover:text-slate-700"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
